import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext.js';
import { fetchQuota } from '../../../services/matchingApi.js';
import { fetchMeetingAudio } from '../../../services/diarizationApi.js';
import { useDiarization } from '../../../hooks/useDiarization.js';
import { DIARIZATION_PHASE, RETRY_FROM } from './diarizationAdapter.js';

/** `required_points` の話者分離のキー (CD-003。PBI-004 §2.4) */
export const DIARIZATION_POINTS_KEY = 'DIARIZATION';

/** 面談の音声の応答から読んだ、話者分離の必要なポイントの区分 (SCR-D-001 §5.2.1) */
export const AUDIO_REQUIRED_KIND = {
    /** 分からない（音声の応答を取得できていない・音声が無い・`required_points` が null＝長さを測っていない音声） */
    unknown: 'unknown',
    /** `required_points` はあるが `DIARIZATION` のキーが無い（サーバがこの面談・この音声の受付を受け付けない） */
    keyMissing: 'key_missing',
    /** 必要なポイントが分かる */
    known: 'known',
};

/**
 * 面談の音声の応答の添付から、話者分離の必要なポイントを読む (SCR-D-001 §5.2.1「画面が使う値」。#1664)。
 *
 * **話者分離の必要なポイントも、`DIARIZATION` のキーがあるかも、面談の音声の応答だけで見る。**
 * 残量の API (`GET /api/matching/quota`) の `required_points.DIARIZATION` は 60 分までの 1 単位の値で、
 * 音声ごとの値ではないため使わない（#1407 の設計の「段 2」。サーバが残量の API から外すのは段 3）。
 * 画面は式も上限も持たず、サーバが返した値をそのまま使う。
 *
 * @param {{required_points?: (Object.<string, number>|null)}|null|undefined} attachment
 *   `GET /meetings/{id}/audio` の `attachment`、または `POST …/audio/confirm` の応答
 * @returns {{kind: string, points: (number|null)}} kind は AUDIO_REQUIRED_KIND の値
 */
export function readAudioRequiredPoints(attachment) {
    const required = attachment?.required_points;
    if (!required || typeof required !== 'object') return { kind: AUDIO_REQUIRED_KIND.unknown, points: null };
    const points = required[DIARIZATION_POINTS_KEY];
    if (typeof points !== 'number') return { kind: AUDIO_REQUIRED_KIND.keyMissing, points: null };
    return { kind: AUDIO_REQUIRED_KIND.known, points };
}

/**
 * AI ポイントと `DIARIZATION` のキーの側から見て、話者分離を含めてよいか (SCR-D-001 §5.2 No.1・§5.2.1)。
 *
 * - `DIARIZATION` のキーが無い: **含めない**（§5.2.1「取得できないとき」②。サーバがこの音声の話者分離を
 *   受け付けないことを意味する——音声の長さが上限を超えている・話者分離の状態が受け付けない等。
 *   画面は理由を区別しない。SCR-M-001 の「残量 > 0」の代替は使わない）
 * - 必要なポイントが分からない（`required_points` が null・音声の応答を取得できていない）: 含めてよい
 *   （null は §5.2.1「画面が使う値」、取得できていないときは同「取得できないとき」③。最終の判定はサーバ）。
 *   含めない側に倒さない: 取得に失敗しただけの面談で、話者分離が黙って行われなくなるため
 * - 残量を取得できない（未取得・取得失敗）: 含めてよい（§5.2.1「取得できないとき」①）
 * - それ以外: 残量 ≧ 必要なポイントのときだけ含める（ちょうど同じなら含める。サーバの判定と同じ境界）
 *
 * @param {{remaining_points?: number}|null} quota 残量の API の応答（残量だけを使う）
 * @param {{kind: string, points: (number|null)}|null|undefined} audioRequired readAudioRequiredPoints の返す形
 * @returns {boolean}
 */
export function canIncludeDiarization(quota, audioRequired) {
    if (audioRequired?.kind === AUDIO_REQUIRED_KIND.keyMissing) return false;
    if (audioRequired?.kind !== AUDIO_REQUIRED_KIND.known) return true;
    if (!quota) return true;
    return quota.remaining_points >= audioRequired.points;
}

/** 話者分離の状態が理由で、確認モーダルで案内せず受付も送らない場合の区分 (SCR-D-001 §6.2) */
export const DIARIZATION_STATE_BLOCK = {
    /** 完了している（サーバは受付を 409「完了しています」で断る） */
    done: 'done',
    /** 失敗していて再実行できない（サーバは受付を 409「やり直せません」で断る） */
    notRetryable: 'not_retryable',
};

/**
 * 話者分離の状態が理由で、含めない（確認モーダルで案内せず、受付も送らない）かを返す
 * (SCR-D-001 §6.2「話者分離を含める条件（話者分離の状態と音声の長さ）」の状態の行。#1645・#1646)。
 *
 * **サーバがその受付を断ると分かっている状態のときだけ止める。** 断られる受付を送ると、受付の失敗の印
 * (§6.3) だけが出て、完了の面談ではタブの中にエラーの文が出ない（印が何も無い場所を指す）。
 *
 * 渡す状態は、音声の確定の後に取り直したもの（§6.2「確認モーダルの行を、画面を開いたときの状態で決めない」。#1646）。
 * 確定でサーバが完了・再実行できない失敗の記録を消すため、ここで止まるのはサーバが記録を消さなかった場合だけ
 *
 * - 完了 → 止める / 失敗で再実行できない → 止める（再実行のボタンを出さないのと同じ条件）
 * - 未実行・再実行できる失敗 → 止めない
 * - 状態を取得できていない（取得の前・取得の失敗。画面の状態は初期値の idle のまま） → 止めない。
 *   止めると、未実行の面談で話者分離が黙って行われない。最終の判定はサーバ
 * - 想定外の status（画面が状態を解釈できていない）も、取得できていないときと同じく止めない
 *   （§6.2 の表の「想定外の状態」の行。サーバの状態は分からないため、最終の判定をサーバに任せる）
 *
 * @param {{phase: string, failure: ({retryable: boolean, unknownStatus: boolean}|null)}|null|undefined} diarization
 * @returns {string|null} DIARIZATION_STATE_BLOCK の値。止めないときは null
 */
export function diarizationStateBlock(diarization) {
    if (!diarization) return null;
    if (diarization.phase === DIARIZATION_PHASE.done) return DIARIZATION_STATE_BLOCK.done;
    if (diarization.phase === DIARIZATION_PHASE.error) {
        if (diarization.failure?.unknownStatus) return null;
        return diarization.failure?.retryable ? null : DIARIZATION_STATE_BLOCK.notRetryable;
    }
    return null;
}

/**
 * 失敗が、役割の判定からやり直せるものか (`failure.retry_from` が `role_judgement`。PBI-004 §6。#1246 段階 4)。
 *
 * この経路のやり直しは、保存済みの音声認識の結果から役割の判定を行い、音声の有無も長さの上限も見ず、
 * AI ポイントを消費しない（0 ポイント。PM の判断 2026-10-12）。
 *
 * @param {{phase: string, failure: ({retryable: boolean, retryFrom: (string|null)}|null)}|null|undefined} diarization
 * @returns {boolean}
 */
export function isRoleJudgementRetry(diarization) {
    return diarization?.phase === DIARIZATION_PHASE.error
        && diarization.failure?.retryable === true
        && diarization.failure?.retryFrom === RETRY_FROM.roleJudgement;
}

/**
 * 「話者分離を再実行」(BTN-D-003) を出すか・押せるか (SCR-D-001 §4.3 BTN-D-003)。
 *
 * - 役割の判定からやり直せる失敗: 面談の音声の応答（`DIARIZATION` のキーの有無・値）を見ずに出し、
 *   AI ポイントの残量に依らず押せる（音声を消した面談・長さの上限を超える音声のある面談でも、サーバは受け付ける。
 *   キーを見ると、サーバが受け付けるのにボタンを隠す）
 * - それ以外: キーが無ければ出さない（サーバがいまの音声の受付を受け付けない。#1664）。
 *   押せるのは AI ポイントとキーの判定 (§5.2.1) を満たすとき
 *
 * 確認モーダル (BTN-D-001) には使わない: モーダルが開くのは音声の確定の直後で、確定でサーバは役割の判定からの
 * やり直しを取り消す（§5.2.1「取り直す時機」④）
 *
 * @param {Object|null|undefined} diarization useDiarization の返す形
 * @param {{remaining_points?: number}|null} quota
 * @param {{kind: string, points: (number|null)}} audioRequired readAudioRequiredPoints の返す形
 * @returns {{canExecute: boolean, retryUnavailable: boolean}}
 */
export function retryAvailability(diarization, quota, audioRequired) {
    if (isRoleJudgementRetry(diarization)) return { canExecute: true, retryUnavailable: false };
    return {
        canExecute: canIncludeDiarization(quota, audioRequired),
        retryUnavailable: audioRequired?.kind === AUDIO_REQUIRED_KIND.keyMissing,
    };
}

/**
 * 活動詳細画面の話者分離（フラグ・AI ポイントの残量・必要なポイント・状態）をまとめる。
 *
 * - フラグは `GET /auth/me` の `features.diarization`（SCR-D-001 §6.1。PBI-004 §2.5）。無効なら何も呼ばない
 * - 残量は確認モーダルを開いたとき・受付の応答の後・結果が出たときに取り直す（§5.2.1「取り直す時機」①②③）。
 *   取り直しに失敗したときは前に取れた値を残す（SCR-M-001 §6.4.4 と同じ）
 * - 必要なポイントと `DIARIZATION` のキーの有無は、面談の音声の応答から読む（§5.2.1。#1664）。
 *   音声のアップロードの部品が受け取った応答（画面を開いたときの取得・アップロードの確定）を
 *   `applyAudioAttachment` で受け取り、受付の応答の後と結果が出たときは自分で取り直す
 *   （サーバは話者分離の状態でキーを載せるかを変える。取り直さないと、処理中に開いた画面で失敗に変わったとき、
 *   キーが無いままに見えて再実行のボタンが出ない）。取り直しに失敗したときは前に取れた値を残す
 * - 話者分離を含めるのは、AI ポイントとキーの判定と、話者分離の状態の条件（音声の確定の後に取り直した状態が
 *   完了・再実行できない失敗のままなら含めない。§6.2。#1646）をどちらも満たすときだけ
 * - 話者分離を含めるかは、確認モーダルで実行を押した時点で決め（`decideInclude`）、
 *   既存の AI 処理が実際に始まるとき（プロンプトの選択の確定）に受け付けさせる（`startIfIncluded`）
 *
 * @param {{meetingId: string}} params
 */
export function useActivityDiarization({ meetingId }) {
    const { currentUser } = useAuth();
    const enabled = currentUser?.features?.diarization === true;

    const [quota, setQuota] = useState(null);
    const loadQuota = useCallback(async () => {
        if (!enabled) return;
        const res = await fetchQuota();
        if (res.ok) setQuota(res.data);
    }, [enabled]);

    // 面談の音声の応答は、どの面談のものかと一緒に持つ。前の面談の応答（キーの有無）を次の面談の判定に使うと、
    // 再実行のボタンを誤って隠す・出すため、ID が違う応答は無いものとして扱う
    // （分からないときの扱い。SCR-D-001 §5.2.1「取得できないとき」③）。
    // 活動の詳細の画面は面談の ID が変わると作り直す (#1693) ため、画面の中ではこの場面は起きないが、
    // このフックを作り直さずに使う呼び出し側のために残す（二重の守り。テストはフックを直接描いて確かめる）
    const [audioState, setAudioState] = useState({ meetingId: null, attachment: null });
    const currentMeetingIdRef = useRef(meetingId);
    useEffect(() => { currentMeetingIdRef.current = meetingId; }, [meetingId]);
    /** 音声のアップロードの部品が受け取った面談の音声の応答（添付。無ければ null）を受け取る */
    const applyAudioAttachment = useCallback((attachment) => {
        setAudioState({ meetingId, attachment: attachment && typeof attachment === 'object' ? attachment : null });
    }, [meetingId]);
    const reloadAudio = useCallback(async () => {
        if (!enabled || !meetingId) return;
        const res = await fetchMeetingAudio(meetingId);
        // 取り直しの間に面談が変わっていたら捨てる（遅れて届いた前の面談の応答で、いまの面談の値を置き換えない）
        if (res.ok && currentMeetingIdRef.current === meetingId) setAudioState({ meetingId, attachment: res.attachment });
    }, [enabled, meetingId]);
    const audioAttachment = audioState.meetingId === meetingId ? audioState.attachment : null;
    const audioRequired = useMemo(() => readAudioRequiredPoints(audioAttachment), [audioAttachment]);

    /** 受付の応答の後・結果が出たときに、残量と面談の音声の応答を取り直す (§5.2.1「取り直す時機」②③) */
    const reloadAfterSettled = useCallback(() => {
        loadQuota();
        reloadAudio();
    }, [loadQuota, reloadAudio]);

    const diarization = useDiarization({ meetingId, enabled, onSettled: reloadAfterSettled });
    const { reload: reloadDiarization } = diarization;

    // 音声の確定の後の状態の取り直しが失敗したか。失敗したら、確認モーダルの判定では状態を「取得できていない」とし、
    // 画面を開いたときに取得した状態に戻らない（§6.2 の表の「状態を取得できていない」の行。#1646）。
    // 完了だった面談では、サーバの記録はもう消えているため、開いたときの完了で判定すると話者分離が黙って行われない
    const [confirmReloadFailed, setConfirmReloadFailed] = useState(false);
    const diarizationForInclude = confirmReloadFailed ? null : diarization;

    const includedRef = useRef(false);

    /**
     * 確認モーダルで実行を押したときに、話者分離を含めるかを決める。
     * AI ポイントとキーの判定 (§5.2.1) と、話者分離の状態の条件 (§6.2) を、どちらも満たすときだけ含める
     */
    const decideInclude = useCallback(() => {
        includedRef.current = enabled
            && canIncludeDiarization(quota, audioRequired)
            && diarizationStateBlock(diarizationForInclude) === null;
    }, [enabled, quota, audioRequired, diarizationForInclude]);

    /** 既存の AI 処理が実際に始まるときに、含めると決めていれば受け付けさせる（1 回だけ） */
    const startIfIncluded = useCallback(() => {
        if (!includedRef.current) return;
        includedRef.current = false;
        diarization.execute();
    }, [diarization]);

    /** 確認モーダルを閉じた（キャンセルした）ときに、含める決定を取り消す */
    const cancelInclude = useCallback(() => { includedRef.current = false; }, []);

    /**
     * 音声の登録（アップロードの確定）の後に、話者分離の状態を取り直す (§5.2.1「取り直す時機」④。#1246 段階 4・#1646)。
     * - 差し替えでサーバは保存済みの音声認識の結果を消し、`retry_from` が役割の判定から音声認識に変わる。
     *   取り直さないと、前の `role_judgement` のまま、音声の応答を見ずに再実行のボタンを出し続ける
     * - 確定でサーバは完了・再実行できない失敗の記録を消す (#1646)。取り直さないと、確認モーダルが開いたときの
     *   完了で判定し、話者分離を作り直せるのに案内せず受付も送らない
     *
     * 呼び出し側は、この Promise が解決してから確認モーダルを開く（取り直しの成否を問わない。§6.2 図 6-1 の下の
     * 「手順 5 の後の取り直し」）。応答の前に開くと、取り直す前の状態で表示と判定が決まる
     *
     * @returns {Promise<void>}
     */
    const reloadAfterAudioConfirmed = useCallback(async () => {
        if (!enabled) return;
        // 応答の本文を読めない等で例外になっても失敗として扱い、呼び出し側が確認モーダルを開けるようにする
        const res = await reloadDiarization().catch(() => null);
        setConfirmReloadFailed(!res?.ok);
    }, [enabled, reloadDiarization]);

    const { canExecute, retryUnavailable } = retryAvailability(diarization, quota, audioRequired);

    // 画面を開いている間に新しく失敗になったか（状態の取り直しで pending から error に変わった）。
    // 読み上げは新しく起きた失敗だけを知らせ、画面を開いたときに既にある失敗は知らせない
    // (SCR-D-001 §7.2。DESIGN の決定 #1643 comment 6394223171 の No.3、PM の判断 comment 6394254669)。
    // 開いたときは idle → error のため、ここでは立たない。error から離れたら下ろす
    const { phase } = diarization;
    const prevPhaseRef = useRef(phase);
    const [failedWhileOpen, setFailedWhileOpen] = useState(false);
    useEffect(() => {
        const prev = prevPhaseRef.current;
        prevPhaseRef.current = phase;
        if (phase !== DIARIZATION_PHASE.error) setFailedWhileOpen(false);
        else if (prev === DIARIZATION_PHASE.pending) setFailedWhileOpen(true);
    }, [phase]);
    // 受付の失敗は、この画面で送った受付の応答で分かったもの（常に新しい）
    const isErrorNew = Boolean(diarization.executeError) || failedWhileOpen;

    return {
        enabled,
        diarization,
        quota,
        loadQuota,
        audioRequired,
        applyAudioAttachment,
        reloadAfterAudioConfirmed,
        // 確認モーダルの判定に使う状態（音声の確定の後の取り直しが失敗したら null＝取得できていない。§6.2。#1646）
        diarizationForInclude,
        // 再実行 (BTN-D-003) を押せるか・出さないか (§4.3。役割の判定からやり直せる失敗では音声の応答を見ない)
        canExecute,
        retryUnavailable,
        // 話者分離のエラーが、この画面を開いている間に起きたか（タブの外の読み上げに使う。#1643）
        isErrorNew,
        decideInclude,
        startIfIncluded,
        cancelInclude,
    };
}
